import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Shield, Users, Activity, AlertTriangle, Settings, LogOut, Lock, Link2, UserCog, ClipboardList, FileCheck } from "lucide-react";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: Activity, exact: true },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/agents", label: "Agents", icon: Shield },
  { to: "/admin/link-agent", label: "Link Agent", icon: Link2, highlight: true },
  { to: "/admin/onboarding", label: "Onboarding", icon: ClipboardList, highlight: true },
  { to: "/admin/pending-changes", label: "Pending Changes", icon: FileCheck, highlight: true },
  { to: "/admin/employees", label: "Employees", icon: UserCog },
  { to: "/admin/webhook-health", label: "Webhook Health", icon: Activity },
  { to: "/admin/quarantine", label: "Quarantine", icon: AlertTriangle },
  { to: "/admin/audit-logs", label: "Audit Logs", icon: Settings },
];

export function AdminLayout() {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem("adminKey") || "");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState("");
  const [keyInput, setKeyInput] = useState("");
  const navigate = useNavigate();

  const verifyAdminKey = async (key) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/admin/webhook-health`, {
        headers: { "X-Admin-Key": key }
      });
      
      if (res.ok) {
        sessionStorage.setItem("adminKey", key);
        setAdminKey(key);
        setIsAuthenticated(true);
        setAuthError("");
      } else {
        setAuthError("Invalid admin key");
        sessionStorage.removeItem("adminKey");
        setIsAuthenticated(false);
      }
    } catch (err) {
      setAuthError("Failed to verify admin key");
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    if (adminKey) {
      verifyAdminKey(adminKey);
    }
  }, [adminKey]);

  const handleLogin = (e) => {
    e.preventDefault();
    verifyAdminKey(keyInput);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminKey");
    setAdminKey("");
    setIsAuthenticated(false);
    setKeyInput("");
  };

  // Login screen - Light theme matching customer dashboard
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-xl border border-gray-200">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-[#4F80F5] rounded-xl flex items-center justify-center shadow-lg">
              <Shield className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 text-center mb-2">Admin Access</h1>
          <p className="text-gray-500 text-center text-sm mb-6">Enter your admin API key to continue</p>
          
          <form onSubmit={handleLogin}>
            <div className="relative mb-4">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Admin API Key"
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4F80F5] focus:border-transparent"
                autoFocus
              />
            </div>
            
            {authError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {authError}
              </div>
            )}
            
            <button
              type="submit"
              className="w-full py-3 bg-[#4F80F5] text-white font-bold rounded-lg hover:bg-[#3D6FE4] transition-all shadow-lg"
            >
              Access Admin Panel
            </button>
          </form>
          
          <p className="text-gray-400 text-xs text-center mt-6">
            Unauthorized access is prohibited and monitored.
          </p>
        </div>
      </div>
    );
  }

  // Admin dashboard - Light theme matching customer dashboard
  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar - Blue like customer dashboard */}
      <aside className="w-64 bg-[#4F80F5] flex flex-col">
        <div className="p-4 border-b border-white/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="text-white font-bold">Calyro Admin</div>
              <div className="text-xs text-white/70">Operations Panel</div>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-3 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.exact}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                  isActive
                    ? "bg-white text-[#4F80F5] shadow-lg"
                    : item.highlight 
                    ? "text-white bg-white/10 hover:bg-white/20 border border-white/30"
                    : "text-white/80 hover:bg-white/10 hover:text-white",
                ].join(" ")
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
              {item.highlight && (
                <span className="ml-auto px-1.5 py-0.5 text-xs bg-[#6EDBC5] rounded text-white font-bold">New</span>
              )}
            </NavLink>
          ))}
        </nav>
        
        <div className="p-3 border-t border-white/20">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-white/80 hover:bg-white/10 hover:text-white transition-all"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col">
        <header className="h-14 border-b border-gray-200 flex items-center justify-between px-6 bg-white shadow-sm">
          <div className="text-gray-800 font-semibold">Admin Dashboard</div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full bg-[#4F80F5] text-white text-xs font-bold">
              ADMIN
            </span>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-y-auto bg-gray-100">
          <Outlet context={{ adminKey }} />
        </main>
      </div>
    </div>
  );
}

export default AdminLayout;
